import React, {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import selectStyles from '@lib/utils/selectStyles';
import mergeCssModules from '@lib/utils/mergeCssModules';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import useTextCroppedTooltip from '@lib/hooks/useTextCroppedTooltip';
import Loader from '@lib/components/CircleLoader/CircleLoader';
import Icon from './Icon';
import defaultStyles from './Default.module.scss';
import themeStyles from './Button.module.scss';

export enum ButtonTypes {
  primaryFilled = 'primary-filled',
  primaryOutlined = 'primary-outlined',
  primaryText = 'primary-text',
  secondaryOutlined = 'secondary-outlined',
  tertiaryFilled = 'tertiary-filled',
  quaternaryOutlined = 'quaternary-outlined',
  negativeOutlined = 'negative-outlined',
  negativeFilled = 'negative-filled',
}

export enum ButtonSizes {
  large = 'large',
  small = 'small',
}

enum ProgressState {
  idle = 'idle',
  progressing = 'progressing',
  completed = 'completed',
}

export type LoaderProps = { [key: string]: string | number };

export interface ButtonProps {
  buttonSize?: ButtonSizes;
  buttonText?: React.ReactNode;
  buttonType?: ButtonTypes;
  className?: string;
  counter?: number;
  classes?: Record<string, string>;
  'data-test-id'?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  isFocused?: boolean;
  isLoading?: boolean;
  leftContent?: React.ReactElement;
  leftContentClassName?: string;
  leftIcon?: string;
  leftIconClassName?: string;
  loaderProps?: LoaderProps;
  navigate?: (to: string) => void;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  rightContent?: React.ReactElement;
  rightContentClassName?: string;
  rightIcon?: string;
  rightIconClassName?: string;
  role?: string;
  singleLoaderWrapperClassName?: string;
  target?: string;
  to?: string;
  type?: 'button' | 'submit' | 'reset';
  useRipple?: boolean;
  progressDuration?: number;
  isProgressActive?: boolean;
  onProgressComplete?: () => void;
  showTooltipOnCrop?: boolean;
}

const createRipple = (event: React.MouseEvent<HTMLElement>) => {
  const button = event.currentTarget;
  const circle = document.createElement('span');
  const diameter = Math.max(button.clientWidth, button.clientHeight);
  const radius = diameter / 2;
  circle.style.width = `${diameter}px`;
  circle.style.height = `${diameter}px`;
  circle.style.left = `${event.nativeEvent.offsetX - radius}px`;
  circle.style.top = `${event.nativeEvent.offsetY - radius}px`;
  circle.classList.add(defaultStyles.ripple);
  const ripple = button.getElementsByClassName(defaultStyles.ripple)[0];
  if (ripple && ripple.parentElement !== null) {
    ripple.parentElement.removeChild(ripple);
  }
  button.appendChild(circle);
};

const Button = forwardRef(
  (props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      buttonSize = ButtonSizes.large,
      buttonText,
      buttonType = ButtonTypes.primaryFilled,
      className,
      classes,
      counter,
      'data-test-id': dataTestId,
      disabled,
      fullWidth,
      isFocused,
      isLoading,
      leftContent,
      leftContentClassName,
      leftIcon,
      leftIconClassName,
      loaderProps,
      onClick,
      rightContent,
      rightContentClassName,
      rightIcon,
      rightIconClassName,
      role,
      singleLoaderWrapperClassName,
      target,
      to,
      type = 'button',
      useRipple = true,
      progressDuration,
      isProgressActive,
      onProgressComplete,
      showTooltipOnCrop,
    } = props;

    const navigate = useNavigate();

    const { ref: textRef, isTooltipDisabled } =
      useTextCroppedTooltip<HTMLSpanElement>(buttonText, {
        disabled: !showTooltipOnCrop,
      });

    const prevIsProgressActiveRef = useRef(isProgressActive);
    const [progressState, setProgressState] = useState(
      isProgressActive ? ProgressState.progressing : ProgressState.idle,
    );

    if (prevIsProgressActiveRef.current !== isProgressActive) {
      prevIsProgressActiveRef.current = isProgressActive;
      setProgressState(
        isProgressActive ? ProgressState.progressing : ProgressState.idle,
      );
    }

    const handleProgressComplete = useCallback(() => {
      setProgressState(ProgressState.completed);
      onProgressComplete?.();
    }, [onProgressComplete]);

    const styles = mergeCssModules(
      defaultStyles,
      selectStyles(themeStyles, classes),
    );

    const renderLoader = (
      <Loader
        color="currentColor"
        size={24}
        strokeWidth={2}
        padding={0}
        {...loaderProps}
      />
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (useRipple) {
        createRipple(event);
      }

      if (to && target === '_blank') {
        window.open(to, '_blank')?.focus();
      } else if (to && navigate) {
        navigate(to);
      } else if (onClick) {
        onClick(event);
      }
    };

    const onMouseUp = (event: React.MouseEvent<HTMLElement>) =>
      event.currentTarget.blur();

    const buttonClassName = classNames(
      {
        [defaultStyles.root]: true,
        [styles.root]: true,
        [styles[buttonType]]: true,
        [styles[buttonSize]]: true,
        [styles.disabled]: disabled,
        [styles.loading]: isLoading,
        [styles.fullWidth]: fullWidth,
        [styles.focused]: isFocused,
        [styles.progressing]: progressState !== ProgressState.idle,
      },
      className,
    );

    const buttonElement = (
      <button
        ref={ref}
        className={buttonClassName}
        data-test-id={dataTestId}
        disabled={disabled || isLoading || progressState !== ProgressState.idle}
        onClick={handleClick}
        onMouseUp={onMouseUp}
        role={role}
        // eslint-disable-next-line react/button-has-type
        type={type}
      >
        {!!progressDuration && progressState !== ProgressState.idle && (
          <div className={defaultStyles.progressBar}>
            <div
              className={defaultStyles.progressFill}
              style={{ animationDuration: `${progressDuration}s` }}
              onAnimationEnd={handleProgressComplete}
            />
            <div className={defaultStyles.progressRemaining} />
          </div>
        )}
        {isLoading && (
          <div
            className={classNames([
              styles.singleLoaderWrapper,
              singleLoaderWrapperClassName,
              defaultStyles.singleLoaderCenterPosition,
            ])}
          >
            {renderLoader}
          </div>
        )}
        <div
          className={classNames(defaultStyles.buttonContent, {
            [defaultStyles.buttonContentHidden]: isLoading,
          })}
        >
          {!!leftContent && (
            <div
              className={classNames(
                defaultStyles.leftContent,
                leftContentClassName,
              )}
            >
              {leftContent}
            </div>
          )}
          {!!leftIcon && (
            <Icon
              icon={leftIcon}
              styles={styles}
              className={leftIconClassName}
              renderLoader={renderLoader}
              position="left"
            />
          )}
          <span
            ref={showTooltipOnCrop ? textRef : undefined}
            className={styles.buttonText}
          >
            {buttonText}
          </span>
          {!!counter && <span className={styles.buttonCounter}>{counter}</span>}
          {!!rightContent && (
            <div
              className={classNames(
                defaultStyles.rightContent,
                rightContentClassName,
              )}
            >
              {rightContent}
            </div>
          )}
          {!!rightIcon && (
            <Icon
              icon={rightIcon}
              styles={styles}
              className={rightIconClassName}
              renderLoader={renderLoader}
              position="right"
            />
          )}
        </div>
      </button>
    );

    if (showTooltipOnCrop) {
      return (
        <Tooltip
          body={buttonText}
          disabled={isTooltipDisabled}
          contentClassName={defaultStyles.tooltipWrapper}
        >
          {buttonElement}
        </Tooltip>
      );
    }

    return buttonElement;
  },
);

export default memo(Button);
